// RequestRows — RRA v2.2 (WEL-180): the requests a case carries, composed as cards.
//
// Rendered by the New Request modal (1-10 cards) and, as a single card, by the "Add a request"
// panel on an open case — so the two ask for a request in exactly the same words. A card is the
// kind of request (the four scopes), the staff's one-line statement of what is being asked, and
// Remove. There are NO animal fields anywhere: an animal's name, kind and breed are a
// post-approval matter (the lease intake or the household animal form).
//
//   cd apps/web && npx tsx --test src/components/rra/RequestRows.test.ts
//
// Every input has a <label htmlFor>. Pure helpers (validation, bodies, caps) live in
// lib/rraRequests.ts.

import { requestLabel } from '@/lib/rraCopy';
import {
  REQUESTS_MAX,
  REQUEST_KINDS,
  REQUEST_STATEMENT_LABEL,
  REQUEST_STATEMENT_MAX,
  REQUEST_STATEMENT_PLACEHOLDER,
  blankRequestRow,
  canAddRow,
  rowProblem,
  statementRequired,
  type RequestRow,
} from '@/lib/rraRequests';
import type { RraScope } from '@/lib/rraStaffClient';

const errorStyle = { color: 'var(--danger)', fontSize: 'var(--text-sm)', margin: 0 } as const;

/** One request: its kind, the statement, and (when there is more than one) Remove. */
export function RequestRowFields(props: {
  row: RequestRow;
  /** Prefix for the ids (unique per card on the page). */
  idPrefix: string;
  /** The card's title — "Request 2". Omit for the single-card panel. */
  heading?: string | undefined;
  onChange: (patch: Partial<Pick<RequestRow, 'scope' | 'statement'>>) => void;
  onRemove?: (() => void) | undefined;
  disabled?: boolean;
  /** Show the "say what they are asking" message when the statement is required and empty. */
  showErrors?: boolean;
}): React.JSX.Element {
  const { row, idPrefix, disabled = false } = props;
  const kindId = `${idPrefix}-kind`;
  const statementId = `${idPrefix}-statement`;
  const errorId = `${idPrefix}-error`;
  const problem = props.showErrors ? rowProblem(row) : null;
  const required = statementRequired(row.scope);
  return (
    <div className="card" data-section="rra-request-row" style={{ background: 'var(--surface-2, transparent)' }}>
      <div className="stack-sm">
        {props.heading || props.onRemove ? (
          <div className="cluster" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)' }}>
            <div style={{ fontWeight: 'var(--weight-medium)' }}>{props.heading ?? ''}</div>
            {props.onRemove ? (
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={disabled}
                aria-label={props.heading ? `Remove ${props.heading}` : 'Remove this request'}
                onClick={props.onRemove}
              >
                Remove
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="field">
          <label className="label" htmlFor={kindId}>What is being requested</label>
          <select
            id={kindId}
            className="input"
            value={row.scope}
            disabled={disabled}
            onChange={(e) => props.onChange({ scope: e.target.value as RraScope })}
          >
            {REQUEST_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor={statementId}>
            {REQUEST_STATEMENT_LABEL}
            {required ? <span aria-hidden="true"> *</span> : null}
          </label>
          <textarea
            id={statementId}
            className="input"
            rows={2}
            maxLength={REQUEST_STATEMENT_MAX}
            value={row.statement}
            disabled={disabled}
            required={required}
            aria-required={required}
            placeholder={REQUEST_STATEMENT_PLACEHOLDER}
            {...(problem ? { 'aria-invalid': 'true' as const, 'aria-describedby': errorId } : {})}
            onChange={(e) => props.onChange({ statement: e.target.value })}
          />
          {problem ? (
            <p id={errorId} role="alert" style={errorStyle}>
              {problem}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The New Request modal's list: 1-10 cards and "+ Add another request". */
export function RequestRows(props: {
  rows: RequestRow[];
  onChange: (rows: RequestRow[]) => void;
  disabled?: boolean;
  showErrors?: boolean;
}): React.JSX.Element {
  const { rows, onChange, disabled = false } = props;
  const several = rows.length > 1;
  return (
    <div className="stack" data-section="rra-request-rows">
      {rows.map((row, i) => (
        <RequestRowFields
          key={row.key}
          row={row}
          idPrefix={`rra-req-${row.key}`}
          heading={requestLabel(i + 1)}
          onChange={(patch) => onChange(rows.map((r) => (r.key === row.key ? { ...r, ...patch } : r)))}
          onRemove={several ? () => onChange(rows.filter((r) => r.key !== row.key)) : undefined}
          disabled={disabled}
          showErrors={props.showErrors ?? false}
        />
      ))}
      <div>
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          disabled={disabled || !canAddRow(rows)}
          onClick={() => onChange([...rows, blankRequestRow()])}
        >
          + Add another request
        </button>
        {!canAddRow(rows) ? (
          <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', marginLeft: 'var(--space-2)' }}>
            A case holds up to {REQUESTS_MAX} requests.
          </span>
        ) : null}
      </div>
    </div>
  );
}
